import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Truck, ChevronRight, Package, Clock, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import LastUpdated from '@/components/ui/LastUpdated';

export default function ShippingCard({ order, onOpenShippingModal }) {
  const isShipping = order.status === 'dalam_pengiriman' || order.status === 'shipping';
  
  // Calculate total variants / items
  const variantsCount = (order.components || []).filter(c => c.inventory_item_id).length;

  return (
    <Link to={`/office/production/${order.id}`}>
      <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-white/60 overflow-hidden bg-white/80  rounded-xl md:rounded-[2rem]">
        <CardContent className="p-4 md:p-6">
          {/* Header Card: Brand & Status */}
          <div className="flex justify-between items-start mb-1 md:mb-4">
              <div className="flex-1 min-w-0 pr-1 md:pr-4">
               <div className="flex items-center gap-1 md:gap-2 mb-0.5 md:mb-1.5 flex-wrap shrink-0 overflow-hidden">
                  <span className="text-[10px] md:text-xs text-slate-400 font-mono font-bold truncate">#{order.order_number}</span>
                  <span className={`text-[8px] md:text-[9px] font-black px-1.5 md:px-2 py-0.5 rounded-full border uppercase tracking-wider whitespace-nowrap ${
                      isShipping ? 'bg-[#F4F8FC] text-blue-700 border-[#D9E6F5]' : 'bg-[#FFFDF3] text-amber-700 border-[#F6E9CC]'
                  }`}>
                    {isShipping ? 'Sedang Dikirim' : 'Menunggu Dikirim'}
                  </span>
               </div>
               <h3 className="font-display text-lg font-black tracking-tight text-[#302621] truncate leading-tight">{order.brand_name}</h3>
               {order.product_name && (
                 <p className="text-[10px] md:text-xs font-medium text-slate-500 truncate mt-0">{order.product_name}</p>
               )}
             </div>
             <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-slate-300 group-hover:text-[#8C6208] transition-colors shrink-0" />
          </div>
          
          {/* Elegant Shipping Progress Illustration */}
          <div className="py-2 md:py-4 relative mt-2 mb-3 px-3 md:px-0">
             {/* Background Path */}
             <div className="absolute top-1/2 left-4 md:left-2 right-4 md:right-2 h-[2px] bg-slate-100 -translate-y-1/2 rounded-full overflow-hidden">
                {isShipping && (
                   <div className="h-full bg-blue-500/20 w-1/2 animate-pulse" />
                )}
             </div>
             
             <div className="relative flex justify-between items-center">
                <div className="flex flex-col items-center gap-1 md:gap-2">
                   <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center border-2 md:border-2 bg-white z-10 transition-colors duration-500 ${isShipping ? 'border-blue-500 text-blue-600' : 'border-amber-500 text-amber-600 shadow-[0_0_10px_rgba(245,158,11,0.2)]'}`}>
                      <Package className="w-3 h-3 md:w-4 md:h-4" />
                   </div>
                   <span className="text-[8px] md:text-[9px] font-bold text-slate-400 absolute -bottom-4 md:-bottom-5">Pabrik</span>
                </div>

                <div className={`flex flex-col items-center z-10 transition-all duration-700 ease-in-out transform ${isShipping ? 'translate-x-4 md:translate-x-12 scale-[1] md:scale-110' : '-translate-x-3 md:-translate-x-8 opacity-60'}`}>
                   <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center bg-white shadow-md border ${isShipping ? 'border-blue-200' : 'border-slate-100'}`}>
                      <Truck className={`w-4 h-4 md:w-5 md:h-5 ${isShipping ? 'text-blue-600' : 'text-slate-400'}`} />
                   </div>
                </div>

                <div className="flex flex-col items-center gap-1 md:gap-2">
                   <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center border-2 md:border-2 bg-white z-10 transition-colors ${isShipping ? 'border-dashed border-blue-300 text-blue-300' : 'border-dashed border-slate-200 text-slate-300'}`}>
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-current" />
                   </div>
                   <span className="text-[8px] md:text-[9px] font-bold text-slate-400 absolute -bottom-4 md:-bottom-5">Klien</span>
                </div>
             </div>
          </div>

          {/* Footer: Deadline, Variants, Last Update */}
          <div className="mt-4 md:mt-8 pt-3 md:pt-4 border-t border-slate-100 flex items-center justify-between gap-1">
             <div className="flex items-center gap-4 md:gap-4 flex-1 min-w-0">
                 <div className="flex flex-col gap-1">
                   <span className="text-[9px] md:text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 md:gap-1">
                      <Clock className="w-3 h-3 md:w-3 md:h-3" /> Deadline
                   </span>
                   {order.deadline ? (
                     <span className="text-xs md:text-xs font-bold text-slate-700 whitespace-nowrap">{new Date(order.deadline).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</span>
                   ) : (
                     <span className="text-[10px] md:text-xs font-medium text-slate-400 italic">Belum diatur</span>
                   )}
                 </div>
                 
                 <div className="flex flex-col gap-1 flex-1 min-w-0">
                   <span className="text-[9px] md:text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 md:gap-1">
                      <Truck className="w-3 h-3 md:w-3 md:h-3" /> Ekspedisi
                   </span>
                   <span className="text-xs md:text-xs font-bold text-slate-700 truncate">{order.shipping_via || order.courier || 'Belum diatur'}</span>
                 </div>
             </div>
             
             <div className="text-right flex flex-col items-end shrink-0 gap-1">
                <span className="text-[9px] md:text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Update</span>
                <div className="scale-90 md:scale-90 origin-right whitespace-nowrap">
                  <LastUpdated date={order.updated_date || order.created_date} />
                </div>
             </div>
          </div>

          {/* Quick Action Button: Atur Pengiriman Langsung */}
          {onOpenShippingModal && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              {order.status === 'siap_kirim' ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenShippingModal(order, false);
                  }}
                  className="w-full py-2.5 px-4 rounded-xl font-bold text-xs bg-gradient-to-r from-[#D4AF37] to-[#8C6208] hover:from-[#c5a028] hover:to-[#7a5507] text-white shadow-xs hover:shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
                >
                  <Truck className="w-3.5 h-3.5" />
                  <span>Atur Pengiriman / Surat Jalan</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenShippingModal(order, true);
                  }}
                  className="w-full py-2.5 px-4 rounded-xl font-bold text-xs bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200/80 shadow-2xs transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
                >
                  <FileText className="w-3.5 h-3.5 text-blue-600" />
                  <span>Lihat / Ubah Data Pengiriman</span>
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}